import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import SharedButton from '../../components/Shared/SharedButton';
import { getERPClient } from '../../services/supplyChainApi';

const EMPTY_LEVEL = {
  uom_id: '',
  child_uom_id: '',
  child_quantity: '',
  factor_to_base: '',
  level_order: '',
  barcode: '',
  gtin: '',
  package_udi_di: '',
  unit_price: '',
  is_default_purchase: false,
  is_default_receiving: false,
  is_default_issue: false,
  is_active: true,
};

const inputCls =
  'w-full border border-gray-300 rounded-[4px] px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-primary h-[30px]';

export default function ItemPackagingTab({ itemId, item }) {
  const { t } = useTranslation();
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uoms, setUoms] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [form, setForm] = useState(EMPTY_LEVEL);
  const [formError, setFormError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editError, setEditError] = useState('');
  const [saving, setSaving] = useState(false);

  const loadLevels = useCallback(async () => {
    setLoading(true);
    try {
      const erpClient = getERPClient();
      const res = await erpClient.getItemPackagingLevels(itemId);
      const list = Array.isArray(res) ? res : (res?.data || []);
      setLevels(list);
    } catch (err) {
      console.error('Failed to load packaging levels:', err);
      toast.error(t('Failed to load packaging levels'));
    } finally {
      setLoading(false);
    }
  }, [itemId, t]);

  useEffect(() => {
    loadLevels();
    (async () => {
      try {
        const erpClient = getERPClient();
        const res = await erpClient.getUoms();
        const list = Array.isArray(res) ? res : (res?.data || []);
        setUoms(list);
      } catch (err) {
        console.error('Failed to load UOMs:', err);
      }
    })();
  }, [loadLevels]);

  const uomsById = useMemo(() => uoms.reduce((acc, u) => {
    if (u.id) acc[u.id] = u;
    return acc;
  }, {}), [uoms]);

  const uomOptions = uoms.map((u) => ({
    value: u.id,
    label: u.name ? `${u.code || ''} - ${u.name}`.replace(/^- /, '') : (u.code || u.id),
  }));

  const stockUomId = item?.stock_uom_id;
  const stockUom = stockUomId ? uomsById[stockUomId] : undefined;

  const effectiveLevels = useMemo(() => {
    const hasBase = levels.some((l) => l.is_base_uom);
    if (hasBase || !stockUomId) return levels;
    return [
      {
        id: `base-${stockUomId}`,
        uom_id: stockUomId,
        uom: stockUom,
        uom_code: stockUom?.code || item?.stock_uom_code || item?.stock_uom?.code || 'Base UOM',
        factor_to_base: 1,
        unit_price: null,
        is_base_uom: true,
        is_active: true,
      },
      ...levels,
    ];
  }, [levels, stockUomId, stockUom, item]);

  const levelOptions = effectiveLevels
    .map((l) => ({
      value: l.uom_id || l.uom?.id,
      label: l.uom_code || l.uom?.code || l.uom?.name_en,
    }))
    .filter((o) => o.value);

  const setValue = (name, value) => setForm((current) => ({ ...current, [name]: value }));
  const setEditValue = (name, value) => setEditForm((current) => ({ ...current, [name]: value }));

  const calculateFactorToBase = (childUomId, childQuantity) => {
    const childLevel = effectiveLevels.find((l) => String(l.uom_id || l.uom?.id) === String(childUomId));
    const quantity = Number(childQuantity);
    const childFactor = Number(childLevel?.factor_to_base);
    if (!childLevel || !Number.isFinite(quantity) || quantity <= 0 || !Number.isFinite(childFactor)) {
      return '';
    }
    return quantity * childFactor;
  };

  const closeForm = () => {
    setIsAdding(false);
    setForm(EMPTY_LEVEL);
    setFormError('');
  };

  const submit = async () => {
    if (!form.uom_id || !form.child_uom_id || Number(form.child_quantity) <= 0 || Number(form.factor_to_base) <= 0) {
      setFormError(t('UOM, child UOM, child quantity, and factor to base are required'));
      return;
    }
    if (form.gtin && !/^\d{14}$/.test(form.gtin)) {
      setFormError(t('GTIN must be exactly 14 digits'));
      return;
    }
    setFormError('');
    setSaving(true);
    try {
      const erpClient = getERPClient();
      await erpClient.createItemPackagingLevel(itemId, {
        ...form,
        child_quantity: Number(form.child_quantity),
        factor_to_base: Number(form.factor_to_base),
        level_order: form.level_order === '' ? effectiveLevels.length : Number(form.level_order),
        barcode: form.barcode || null,
        gtin: form.gtin || null,
        unit_price: form.unit_price !== '' ? Number(form.unit_price) : null,
        package_udi_di: form.package_udi_di || null,
      });
      toast.success(t('Packaging level created successfully'));
      closeForm();
      loadLevels();
    } catch (err) {
      console.error('Failed to create packaging level:', err);
      const msg = err.response?.data?.message || t('Failed to create packaging level');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const startEditing = (level) => {
    setEditingId(level.id);
    setEditForm({
      child_uom_id: level.child_uom_id || '',
      child_quantity: level.child_quantity ?? '',
      factor_to_base: level.factor_to_base ?? '',
      level_order: level.level_order ?? '',
      barcode: level.barcode || '',
      gtin: level.gtin || '',
      package_udi_di: level.package_udi_di || '',
      unit_price: level.unit_price != null ? level.unit_price : '',
      is_default_purchase: !!level.is_default_purchase,
      is_default_receiving: !!level.is_default_receiving,
      is_default_issue: !!level.is_default_issue,
      is_active: level.is_active !== false,
    });
    setEditError('');
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditForm({});
    setEditError('');
  };

  const submitEdit = async (level) => {
    if (Number(editForm.child_quantity) <= 0 || Number(editForm.factor_to_base) <= 0) {
      setEditError(t('Child quantity and factor to base are required'));
      return;
    }
    if (editForm.gtin && !/^\d{14}$/.test(editForm.gtin)) {
      setEditError(t('GTIN must be exactly 14 digits'));
      return;
    }
    setEditError('');
    setSaving(true);
    try {
      const erpClient = getERPClient();
      await erpClient.updateItemPackagingLevel(itemId, level.id, {
        child_uom_id: editForm.child_uom_id || null,
        child_quantity: Number(editForm.child_quantity),
        factor_to_base: Number(editForm.factor_to_base),
        level_order: editForm.level_order !== '' ? Number(editForm.level_order) : null,
        barcode: editForm.barcode || null,
        gtin: editForm.gtin || null,
        package_udi_di: editForm.package_udi_di || null,
        unit_price: editForm.unit_price !== '' ? Number(editForm.unit_price) : null,
        is_default_purchase: editForm.is_default_purchase,
        is_default_receiving: editForm.is_default_receiving,
        is_default_issue: editForm.is_default_issue,
        is_active: editForm.is_active,
      });
      toast.success(t('Packaging level updated successfully'));
      cancelEditing();
      loadLevels();
    } catch (err) {
      console.error('Failed to update packaging level:', err);
      const msg = err.response?.data?.message || t('Failed to update packaging level');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const updateUnitPrice = async (level, value) => {
    const val = value === '' ? null : Number(value);
    try {
      const erpClient = getERPClient();
      await erpClient.updateItemPackagingLevel(itemId, level.id, { unit_price: val });
      toast.success(t('Unit price updated'));
      loadLevels();
    } catch (err) {
      console.error('Failed to update unit price:', err);
      toast.error(t('Failed to update unit price'));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <i className="pi pi-spin pi-spinner text-3xl text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="font-semibold text-gray-800">{item?.name_en || item?.short_name || ''}</h3>
          {item?.name_ar && <p className="text-xs text-gray-500" dir="rtl">{item.name_ar}</p>}
        </div>
        {!isAdding && (
          <SharedButton
            type="button"
            label={t('Add Packaging Level')}
            buttonType="primary"
            icon="pi pi-plus"
            onClick={() => setIsAdding(true)}
          />
        )}
      </div>

      <div className="overflow-x-auto border border-gray-200 rounded-md">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              {['UOM', 'Child UOM', 'Child Qty', 'Factor to Base', 'Unit Price', 'GTIN', 'Package UDI/DI', 'Defaults', 'Status', ''].map((label) => (
                <th key={label} className="px-3 py-2 text-start font-medium">{t(label)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {effectiveLevels.map((level) => {
              const isEditing = editingId === level.id && !level.is_base_uom;
              const isBase = level.is_base_uom;
              return (
                <tr key={level.id || level.uom_id || level.uom?.id} className="border-t border-gray-100">
                  <td className="px-3 py-2 font-semibold">
                    {level.uom_code || level.uom?.code || ''}
                    {isBase && <span className="ms-1 text-[10px] text-primary">({t('Base - automatic')})</span>}
                  </td>

                  {isEditing ? (
                    <>
                      <td className="px-3 py-2">
                        <select value={editForm.child_uom_id || ''} onChange={(e) => setEditValue('child_uom_id', e.target.value)} className={inputCls}>
                          <option value="">{t('Select...')}</option>
                          {levelOptions.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          min="1"
                          className={inputCls}
                          value={editForm.child_quantity ?? ''}
                          onChange={(e) => setEditValue('child_quantity', e.target.value)}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input type="number" className={`${inputCls} bg-gray-100`} value={editForm.factor_to_base ?? ''} readOnly />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number" step="0.01" min="0"
                          className={inputCls}
                          value={editForm.unit_price ?? ''}
                          onChange={(e) => setEditValue('unit_price', e.target.value)}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="text" maxLength={14}
                          className={inputCls}
                          value={editForm.gtin || ''}
                          onChange={(e) => setEditValue('gtin', e.target.value.replace(/\D/g, ''))}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          className={inputCls}
                          value={editForm.package_udi_di || ''}
                          onChange={(e) => setEditValue('package_udi_di', e.target.value)}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-col gap-0.5 text-[11px]">
                          <label className="flex items-center gap-1 cursor-pointer">
                            <input type="checkbox" checked={editForm.is_default_receiving} onChange={(e) => setEditValue('is_default_receiving', e.target.checked)} />
                            {t('Receiving')}
                          </label>
                          <label className="flex items-center gap-1 cursor-pointer">
                            <input type="checkbox" checked={editForm.is_default_issue} onChange={(e) => setEditValue('is_default_issue', e.target.checked)} />
                            {t('Issue')}
                          </label>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <label className="flex items-center gap-1 text-[11px] cursor-pointer">
                          <input type="checkbox" checked={editForm.is_active} onChange={(e) => setEditValue('is_active', e.target.checked)} />
                          {editForm.is_active ? t('Active') : t('Inactive')}
                        </label>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1.5">
                          <button type="button" onClick={() => submitEdit(level)} disabled={saving} className="text-green-600 hover:text-green-800" title={t('Save')}>
                            <i className="pi pi-check text-sm" />
                          </button>
                          <button type="button" onClick={cancelEditing} className="text-red-500 hover:text-red-700" title={t('Cancel')}>
                            <i className="pi pi-times text-sm" />
                          </button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-3 py-2">{level.child_uom_code || level.child_uom?.code || '—'}</td>
                      <td className="px-3 py-2">{level.child_quantity ?? '—'}</td>
                      <td className="px-3 py-2 font-semibold">{level.factor_to_base ?? 1}</td>
                      <td className="px-3 py-2">
                        <input
                          type="number" step="0.01" min="0"
                          defaultValue={level.unit_price != null ? level.unit_price : ''}
                          onBlur={(e) => updateUnitPrice(level, e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') updateUnitPrice(level, e.target.value);
                          }}
                          className="w-20 border border-gray-300 rounded-[4px] px-1 py-0.5 text-[12px] focus:outline-none h-[26px]"
                        />
                      </td>
                      <td className="px-3 py-2">{level.gtin || '—'}</td>
                      <td className="px-3 py-2">{level.package_udi_di || '—'}</td>
                      <td className="px-3 py-2">
                        {[
                          level.is_default_purchase && 'Purchase',
                          level.is_default_receiving && 'Receiving',
                          level.is_default_issue && 'Issue',
                        ].filter(Boolean).join(', ') || '—'}
                      </td>
                      <td className="px-3 py-2">{level.is_active === false ? t('Inactive') : t('Active')}</td>
                      <td className="px-3 py-2">
                        {!isBase && (
                          <button type="button" onClick={() => startEditing(level)} className="text-blue-500 hover:text-blue-700" title={t('Edit')}>
                            <i className="pi pi-pencil" />
                          </button>
                        )}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
            {editError && (
              <tr><td colSpan="10" className="px-3 py-2 text-[12px] text-red-500 text-center">{editError}</td></tr>
            )}
            {!effectiveLevels.length && (
              <tr>
                <td colSpan="10" className="px-3 py-8 text-center text-gray-500">
                  {t('No packaging levels configured. Select a Stock UOM on the item first.')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {isAdding && (
        <div className="border border-primary/20 bg-primary/5 rounded-md p-3 space-y-3">
          <div className="flex justify-between items-center">
            <h4 className="font-semibold flex items-center gap-2 text-sm">
              <i className="pi pi-box text-primary" />
              {t('New Packaging Level')}
            </h4>
            <button type="button" onClick={closeForm}><i className="pi pi-times" /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-700">{t('Package UOM')} *</label>
              <select className={inputCls} value={form.uom_id} onChange={(e) => setValue('uom_id', e.target.value)}>
                <option value="">{t('Select...')}</option>
                {uomOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-700">{t('Child UOM')} *</label>
              <select
                className={inputCls}
                value={form.child_uom_id}
                onChange={(e) => {
                  const value = e.target.value;
                  setForm((current) => ({
                    ...current,
                    child_uom_id: value,
                    factor_to_base: calculateFactorToBase(value, current.child_quantity),
                  }));
                }}
              >
                <option value="">{t('Select...')}</option>
                {levelOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-700">{t('Child Quantity')} *</label>
              <input
                type="number" min="1"
                className={inputCls}
                value={form.child_quantity}
                onChange={(e) => {
                  const value = e.target.value;
                  setForm((current) => ({
                    ...current,
                    child_quantity: value,
                    factor_to_base: calculateFactorToBase(current.child_uom_id, value),
                  }));
                }}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-700">{t('Factor to Base (Automatic)')}</label>
              <input type="number" className={`${inputCls} bg-gray-100`} value={form.factor_to_base} readOnly />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-700">{t('Level Order')}</label>
              <input type="number" min="0" className={inputCls} value={form.level_order} onChange={(e) => setValue('level_order', e.target.value)} />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-700">{t('Barcode')}</label>
              <input type="text" className={inputCls} value={form.barcode} onChange={(e) => setValue('barcode', e.target.value)} />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-700">{t('Unit Price')}</label>
              <input
                type="number" step="0.01" min="0"
                className={inputCls}
                value={form.unit_price}
                onChange={(e) => setValue('unit_price', e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-700">{t('GTIN-14')}</label>
              <input
                type="text" maxLength={14}
                className={inputCls}
                value={form.gtin}
                onChange={(e) => setValue('gtin', e.target.value.replace(/\D/g, ''))}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-700">{t('Package UDI/DI')}</label>
              <input
                type="text"
                className={inputCls}
                value={form.package_udi_di}
                onChange={(e) => setValue('package_udi_di', e.target.value)}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
            <label className="flex items-center gap-1 text-xs cursor-pointer">
              <input type="checkbox" checked={form.is_default_receiving} onChange={(e) => setValue('is_default_receiving', e.target.checked)} />
              {t('Default Receiving')}
            </label>
            <label className="flex items-center gap-1 text-xs cursor-pointer">
              <input type="checkbox" checked={form.is_default_issue} onChange={(e) => setValue('is_default_issue', e.target.checked)} />
              {t('Default Issue')}
            </label>
            <label className="flex items-center gap-1 text-xs cursor-pointer">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setValue('is_active', e.target.checked)} />
              {t('Active')}
            </label>
          </div>
          {formError && <p className="text-xs text-red-500">{formError}</p>}
          <div className="flex justify-end gap-2">
            <SharedButton
              type="button"
              label={t('Cancel')}
              buttonType="reset"
              onClick={closeForm}
            />
            <SharedButton
              type="button"
              label={t('Save Packaging Level')}
              buttonType="primary"
              onClick={submit}
              loading={saving}
            />
          </div>
        </div>
      )}
    </div>
  );
}